import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const BAND_COLOR = { high: "#00C805", medium: "#F59E0B", low: "#FF3B30" };

/**
 * Micro-chart of a bot's confidence score over time. Renders nothing until
 * the bot has at least 2 history entries — freshly-created bots would show
 * a flat line otherwise. Shown inside the bot card next to the confidence
 * badge on demand (Expand button toggle in BotsPage).
 */
export default function BotConfidenceHistory({ botId }) {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/bots/${botId}/confidence/history`)
      .then((r) => { if (!cancelled) setHistory(r.data.history || []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [botId]);

  if (history === null) return null;
  const scored = history.filter((h) => h.score != null);
  if (scored.length < 2) {
    return (
      <div
        className="mono text-[10px] text-zinc-500 mt-1"
        data-testid={`bot-confidence-history-${botId}`}
      >
        Not enough data — first snapshot recorded {scored.length ? "just now" : "on next signal cycle"}.
      </div>
    );
  }

  const last = scored[scored.length - 1];
  const stroke = BAND_COLOR[last.band] || "#a1a1aa";
  const data = scored.map((s, i) => ({ i, score: s.score, band: s.band, at: s.at }));

  return (
    <div
      className="mt-2 border border-zinc-800 rounded-sm p-2 bg-zinc-950/60"
      data-testid={`bot-confidence-history-${botId}`}
    >
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest mb-1">
        Confidence · {scored.length} snapshots
      </div>
      <div className="h-14">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <YAxis hide domain={[0, 100]} />
            <ReferenceLine y={75} stroke="#00C805" strokeDasharray="1 2" strokeOpacity={0.3} />
            <ReferenceLine y={50} stroke="#F59E0B" strokeDasharray="1 2" strokeOpacity={0.3} />
            <Tooltip
              contentStyle={{
                background: "#18181b",
                border: "1px solid #27272a",
                borderRadius: 2,
                fontFamily: "JetBrains Mono",
                fontSize: 11,
              }}
              formatter={(v, _n, p) => [`${v}% (${p?.payload?.band})`, "Score"]}
              labelFormatter={(_, payload) => {
                const at = payload?.[0]?.payload?.at;
                return at ? new Date(at).toLocaleString() : "";
              }}
              labelStyle={{ color: "#a1a1aa" }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke={stroke}
              strokeWidth={2}
              dot={{ r: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
